import React, { useContext, useState, useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Box,
  Divider,
  Text,
  Flex,
} from "@chakra-ui/core";

import OrderItem from "components/food/OrderItem";
import ShoppingBagOutline from "svg/icons/shopping-bag.svg";

import { formatPrice, getPrice, calcStripeFee } from "utils/shared/prices";
import useWindowSize from "hooks/useWindowSize";

import { ContextStore } from "context/ContextStore";
import { useRouter } from "next/router";

import useAuth from "context/Auth";

const Basket = (props) => {
  const {
    basketOrders,
    isBasketOpen,
    setBasketOpen,
    isStoreManuallyClosed,
    isDashboardOnline,
    isOpeningTime,
  } = useContext(ContextStore);
  const { user, isAuthenticated } = useAuth();

  const subTotal = basketOrders.reduce((total, order) => {
    return total + getPrice(order);
  }, 0);

  const frais = calcStripeFee(subTotal);
  const total = Number(subTotal + frais);

  const windowSize = useWindowSize();
  const mobileHeight = windowSize.height + "px";

  const router = useRouter();

  const handleValidateOrder = () => {
    if (isAuthenticated) {
      setBasketOpen(false);
      router.push("/paiement");
    } else {
      setBasketOpen(false);
      router.push("/connexion");
    }
  };

  const defaultButtonText = "Confirmer ma commande";

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [buttonText, setButtonText] = useState(defaultButtonText);

  useEffect(() => {
    const shouldBeDisabled = basketOrders.length === 0 || !isOpeningTime || isStoreManuallyClosed || !isDashboardOnline;

    setIsButtonDisabled(shouldBeDisabled);

    if (basketOrders.length === 0) {
      setButtonText("Panier vide");
      return;
    } else if (!isOpeningTime) {
      setButtonText("Restaurant fermé");
      return;
    } else if (isStoreManuallyClosed) {
      setButtonText("Fermé temporairement");
      return;
    } else if (!isDashboardOnline) {
      setButtonText("Indisponible momentanément");
      return;
    } else {
      setButtonText(defaultButtonText);
    }
  }, [basketOrders, isOpeningTime, isStoreManuallyClosed, isDashboardOnline]);

  return (
    <>
      <Button variantColor="brand" variant="solid" rounded="lg" onClick={() => setBasketOpen(true)} shadow="md">
        <Box as={ShoppingBagOutline} size={18} mr="1" />
        {formatPrice(subTotal)}
      </Button>
      <Drawer
        placement="right"
        size="sm"
        onClose={() => setBasketOpen(false)}
        isOpen={isBasketOpen}
        scrollBehavior="inside"
      >
        <DrawerOverlay opacity={isBasketOpen ? "0.4" : "0"} transition="opacity 200ms ease-in-out" />
        <DrawerContent>
          <DrawerCloseButton mt="2" bg="gray.100" _hover={{ bg: "gray.200" }} _active={{ bg: "gray.300" }} />
          <DrawerHeader borderBottomWidth="1px">Votre commande</DrawerHeader>
          <DrawerBody
            p="6"
            py="4"
            maxHeight={{
              base: `calc(${mobileHeight} - 8.625rem)`,
            }}
          >
            {basketOrders.length === 0 ? <p>Votre panier est vide</p> : null}
            {basketOrders.length > 0
              ? basketOrders.map((order, index) => (
                  <OrderItem key={index} price={getPrice(order)} index={index} order={order} />
                ))
              : null}
            {basketOrders.length > 0 ? (
              <>
                <Divider mb="6" />
                <Flex align="center" justify="space-between" mb="1">
                  <Text fontSize="md" fontWeight="normal" color="gray.800">
                    Sous-total
                  </Text>
                  <Text fontSize="md" fontWeight="normal" color="gray.800">
                    {formatPrice(subTotal)}
                  </Text>
                </Flex>

                <Flex align="center" justify="space-between" mb="1">
                  <Text fontSize="md" fontWeight="normal" color="gray.800">
                    Frais de paiement
                  </Text>
                  <Text fontSize="md" fontWeight="normal" color="gray.800">
                    {formatPrice(frais)}
                  </Text>
                </Flex>

                <Flex align="center" justify="space-between" mb="1">
                  <Text fontSize="md" fontWeight="semibold" color="gray.800">
                    Total
                  </Text>
                  <Text fontSize="md" fontWeight="semibold" color="gray.800">
                    {formatPrice(total)}
                  </Text>
                </Flex>
              </>
            ) : null}
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <Button mr={3} rounded="lg" onClick={() => setBasketOpen(false)} display={{ base: "none", sm: "inherit" }}>
              Fermer
            </Button>
            <Button
              variantColor="brand"
              w="full"
              rounded="lg"
              shadow="md"
              onClick={() => {
                handleValidateOrder();
              }}
              isDisabled={isButtonDisabled}
            >
              {buttonText}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Basket;
